import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'

const NicknamesContext = createContext(null)

export function AccountNicknamesProvider({ children }) {
  const { user } = useAuth()
  const [settings, setSettings] = useState({}) // { accountNumber: { nickname, custom_alloc_pct, custom_risk_pct, notes } }

  const fetchAll = useCallback(async () => {
    if (!user) return
    const { data } = await supabase
      .from('account_nicknames')
      .select('account_number, nickname, custom_alloc_pct, custom_risk_pct, notes')
      .eq('user_id', user.id)
    if (data) {
      const map = {}
      data.forEach(row => {
        map[row.account_number] = {
          nickname: row.nickname || null,
          custom_alloc_pct: row.custom_alloc_pct,
          custom_risk_pct: row.custom_risk_pct,
          notes: row.notes || '',
        }
      })
      setSettings(map)
    }
  }, [user])

  useEffect(() => { fetchAll() }, [fetchAll])

  const getNickname = useCallback((accountNumber) => {
    const entry = settings[accountNumber]
    return (entry && entry.nickname) || `···${accountNumber.slice(-4)}`
  }, [settings])

  const getAccountSettings = useCallback((accountNumber) => {
    return settings[accountNumber] || { nickname: null, custom_alloc_pct: null, custom_risk_pct: null, notes: '' }
  }, [settings])

  const setNickname = useCallback(async (accountNumber, nickname) => {
    if (!user) return
    const trimmed = (nickname || '').trim() || null

    await supabase
      .from('account_nicknames')
      .upsert(
        { user_id: user.id, account_number: accountNumber, nickname: trimmed, updated_at: new Date().toISOString() },
        { onConflict: 'user_id,account_number' }
      )
    setSettings(prev => ({
      ...prev,
      [accountNumber]: { ...(prev[accountNumber] || {}), nickname: trimmed },
    }))
  }, [user])

  const updateAccountSettings = useCallback(async (accountNumber, updates) => {
    if (!user) return
    await supabase
      .from('account_nicknames')
      .upsert(
        { user_id: user.id, account_number: accountNumber, ...updates, updated_at: new Date().toISOString() },
        { onConflict: 'user_id,account_number' }
      )
    setSettings(prev => ({
      ...prev,
      [accountNumber]: { ...(prev[accountNumber] || {}), ...updates },
    }))
  }, [user])

  return (
    <NicknamesContext.Provider value={{ settings, getNickname, getAccountSettings, setNickname, updateAccountSettings }}>
      {children}
    </NicknamesContext.Provider>
  )
}

export function useAccountNicknames() {
  const ctx = useContext(NicknamesContext)
  if (!ctx) throw new Error('useAccountNicknames must be used within AccountNicknamesProvider')
  return ctx
}
